import LinkIcon from '@mui/icons-material/Link';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Grid,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import moment from 'moment';
import { useCallback, useEffect, useState } from 'react';
import { useNotify, useRecordContext } from 'react-admin';
import { DEFAULT_API_ADMIN } from '../../constants';
import {
  BillingSource,
  RECEIVABLE_FILTER_ALL,
  RECEIVABLE_STATUS_LABELS,
  RECEIVABLE_TYPE_LABELS,
  ReceivableStatus,
  ReceivableType,
} from '../../models';
import { httpClientReactAdmin } from '../../lib';

/**
 * Ponte com o Gestão Click. A aba não guarda nada do ERP no nosso banco — só o vínculo
 * (`place.gestaoClickId`). Tudo que aparece aqui é lido ao vivo.
 */

const STATUS_FILTERS = [
  { id: RECEIVABLE_FILTER_ALL, name: 'Todos' },
  { id: ReceivableStatus.OPEN, name: 'Em aberto' },
  { id: ReceivableStatus.OVERDUE, name: 'Em atraso' },
  { id: ReceivableStatus.PAID, name: 'Pago' },
];

const TYPE_FILTERS = [
  { id: RECEIVABLE_FILTER_ALL, name: 'Todos' },
  { id: ReceivableType.MONTHLY, name: 'Mensalidade' },
  { id: ReceivableType.INSTALLATION, name: 'Implantação' },
  { id: ReceivableType.UNKNOWN, name: 'Não identificado' },
];

const brl = value =>
  (value ?? 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const asDate = value => (value ? moment(value).format('DD/MM/YYYY') : '—');

const errorMessage = (error, fallback) =>
  error?.body?.message || error?.message || fallback;

const Field = ({ label, children }) => (
  <Box sx={{ display: 'flex', gap: 1, alignItems: 'baseline', mb: 0.5 }}>
    <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 120 }}>{label}</Typography>
    <Typography variant="body2" color="text.secondary">{children ?? '—'}</Typography>
  </Box>
);

/**
 * De onde o valor veio. O cadastro do posto tem prioridade; a Receita cobre o que estiver em branco
 * — hoje razão social e inscrição estadual estão preenchidas em menos de 10% dos postos.
 */
const Origin = ({ source }) => {
  if (!source) return null;
  const fromPlace = source === 'place';
  return (
    <Chip
      size="small"
      variant="outlined"
      color={fromPlace ? 'primary' : 'default'}
      label={fromPlace ? 'do cadastro' : 'da Receita'}
      sx={{ ml: 1, height: 18, fontSize: '0.68rem' }}
    />
  );
};

const SectionTitle = ({ children, action }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1.5 }}>
    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{children}</Typography>
    {action}
  </Box>
);

/** Estado inicial: o posto ainda não tem cliente no ERP. */
const NotLinked = ({ placeId, contract, stores, onChanged }) => {
  const notify = useNotify();
  const [storeId, setStoreId] = useState('');
  const [candidates, setCandidates] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  const loadPreview = async () => {
    setBusy(true);
    try {
      const { json } = await httpClientReactAdmin(
        `${DEFAULT_API_ADMIN}/places/${placeId}/gestao-click/preview`
      );
      setPreview(json);
    } catch (error) {
      notify(errorMessage(error, 'Não foi possível consultar o CNPJ na Receita'), { type: 'warning' });
    } finally {
      setBusy(false);
    }
  };

  const search = async () => {
    setBusy(true);
    try {
      const { json } = await httpClientReactAdmin(
        `${DEFAULT_API_ADMIN}/places/${placeId}/gestao-click/search`
      );
      setCandidates(json.customers || []);
      if (!json.customers?.length) notify('Nenhum cliente com esse CNPJ no Gestão Click', { type: 'info' });
    } catch (error) {
      notify(errorMessage(error, 'Não foi possível buscar no Gestão Click'), { type: 'warning' });
    } finally {
      setBusy(false);
    }
  };

  const link = async gestaoClickId => {
    setBusy(true);
    try {
      await httpClientReactAdmin(`${DEFAULT_API_ADMIN}/places/${placeId}/gestao-click/link`, {
        method: 'POST',
        body: JSON.stringify({ gestaoClickId: Number(gestaoClickId) }),
      });
      notify('Posto vinculado ao Gestão Click', { type: 'success' });
      onChanged();
    } catch (error) {
      notify(errorMessage(error, 'Não foi possível vincular'), { type: 'warning' });
    } finally {
      setBusy(false);
    }
  };

  const create = async () => {
    setBusy(true);
    try {
      await httpClientReactAdmin(`${DEFAULT_API_ADMIN}/places/${placeId}/gestao-click/customer`, {
        method: 'POST',
        body: JSON.stringify({ storeId }),
      });
      notify('Cliente criado no Gestão Click', { type: 'success' });
      onChanged();
    } catch (error) {
      notify(errorMessage(error, 'Não foi possível criar o cliente'), { type: 'warning' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Alert severity="info" sx={{ mb: 2 }}>
        <AlertTitle>Posto ainda não vinculado</AlertTitle>
        Procure pelo CNPJ antes de criar — cliente duplicado no Gestão Click é trabalhoso de desfazer.
      </Alert>

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Field label="Posto">{contract?.name}</Field>
        <Field label="CNPJ">{contract?.cnpj}</Field>
        <Field label="E-mail de cobrança">{contract?.emailBill}</Field>

        <Box sx={{ display: 'flex', gap: 1, mt: 2, flexWrap: 'wrap' }}>
          <Button variant="outlined" startIcon={<SearchIcon />} onClick={search} disabled={busy || !contract?.cnpj}>
            Procurar pelo CNPJ
          </Button>
        </Box>
        {!contract?.cnpj && (
          <Typography variant="caption" color="error">
            Cadastre o CNPJ do posto na aba Identificação antes de continuar.
          </Typography>
        )}
      </Paper>

      {candidates?.length > 0 && (
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <SectionTitle>Encontrado no Gestão Click</SectionTitle>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Nome</TableCell>
                <TableCell>CNPJ</TableCell>
                <TableCell>Situação</TableCell>
                <TableCell align="right">Ação</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {candidates.map(customer => (
                <TableRow key={customer.id}>
                  <TableCell>{customer.id}</TableCell>
                  <TableCell>{customer.corporateName || customer.name}</TableCell>
                  <TableCell>{customer.document}</TableCell>
                  <TableCell>
                    <Chip size="small" label={customer.isActive ? 'Ativo' : 'Inativo'} color={customer.isActive ? 'success' : 'default'} />
                  </TableCell>
                  <TableCell align="right">
                    <Button size="small" startIcon={<LinkIcon />} onClick={() => link(customer.id)} disabled={busy}>
                      Vincular
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}

      {candidates?.length === 0 && (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <SectionTitle>Criar cliente no Gestão Click</SectionTitle>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Razão social, inscrição estadual e endereço vêm da Receita Federal pelo CNPJ — o cadastro do
            posto não guarda esses campos e o Gestão Click precisa deles para emitir nota.
          </Typography>

          {!preview && (
            <Button variant="outlined" onClick={loadPreview} disabled={busy}>
              Conferir dados na Receita
            </Button>
          )}

          {preview && (
            <>
              <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: 'action.hover' }}>
                <Field label="Nome de exibição">{preview.willSend?.displayName || contract?.name}</Field>
                <Field label="Razão Social">
                  {preview.willSend?.corporateName}
                  <Origin source={preview.willSend?.corporateNameSource} />
                </Field>
                <Field label="Inscrição Estadual">
                  {preview.willSend?.stateRegistration}
                  <Origin source={preview.willSend?.stateRegistrationSource} />
                </Field>
                <Field label="Nome Fantasia (Receita)">{preview.registry.tradeName}</Field>
                <Field label="Situação">{preview.registry.status}</Field>
                <Field label="Endereço">
                  {[preview.registry.address.street, preview.registry.address.number,
                    preview.registry.address.neighborhood].filter(Boolean).join(', ')}
                </Field>
                <Field label="Cidade">
                  {preview.registry.address.city}/{preview.registry.address.state} — CEP {preview.registry.address.zipCode}
                </Field>
                <Field label="E-mail">{preview.email}</Field>
                <Field label="Telefone">{preview.phone}</Field>
              </Paper>

              {preview.registry.status !== 'Ativa' && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  CNPJ com situação cadastral <strong>{preview.registry.status}</strong> na Receita.
                </Alert>
              )}
              {!preview.cityResolved && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  Não foi possível casar a cidade com o cadastro do Gestão Click. O cliente será criado sem
                  cidade e ela precisará ser preenchida no ERP.
                </Alert>
              )}

              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                A loja define em qual conta bancária o boleto será emitido. Não há padrão — a escolha é obrigatória.
              </Typography>
              <TextField
                select
                size="small"
                label="Loja"
                value={storeId}
                onChange={e => setStoreId(e.target.value)}
                sx={{ width: 260, mb: 2 }}
              >
                {(stores || []).map(store => (
                  <MenuItem key={store.id} value={store.id}>{store.name}</MenuItem>
                ))}
              </TextField>
              <Box>
                <Button variant="contained" startIcon={<PersonAddIcon />} onClick={create} disabled={busy || !storeId}>
                  Criar no Gestão Click
                </Button>
              </Box>
            </>
          )}
        </Paper>
      )}
    </>
  );
};

/** Cobranças do posto, lidas ao vivo do ERP. */
const Receivables = ({ placeId }) => {
  const notify = useNotify();
  const [status, setStatus] = useState(RECEIVABLE_FILTER_ALL);
  const [type, setType] = useState(RECEIVABLE_FILTER_ALL);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { json } = await httpClientReactAdmin(
        `${DEFAULT_API_ADMIN}/places/${placeId}/gestao-click/receivables?status=${status}&type=${type}`
      );
      setData(json);
    } catch (error) {
      notify(errorMessage(error, 'Não foi possível carregar as cobranças'), { type: 'warning' });
    } finally {
      setLoading(false);
    }
    // notify é estável no react-admin; incluir causaria recarga em loop
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [placeId, status, type]);

  useEffect(() => { load(); }, [load]);

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <SectionTitle
        action={
          <Button size="small" startIcon={<RefreshIcon />} onClick={load} disabled={loading}>Atualizar</Button>
        }
      >
        Contas a Receber
      </SectionTitle>

      {/* Filtros em linha propria: junto do titulo, os dois selects mais o botao estouravam a
          largura do cartao e o "Atualizar" vazava para fora da borda.

          Largura FIXA, nao minWidth: dentro do TabbedForm do react-admin os FormControl herdam
          width 100%, entao minWidth nao segura nada e cada select ocupava a linha inteira. */}
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
        <TextField
          select size="small" label="Tipo" value={type}
          onChange={e => setType(e.target.value)}
          sx={{ width: 200, flex: '0 0 auto' }}
        >
          {TYPE_FILTERS.map(option => (
            <MenuItem key={option.id} value={option.id}>{option.name}</MenuItem>
          ))}
        </TextField>
        <TextField
          select size="small" label="Status" value={status}
          onChange={e => setStatus(e.target.value)}
          sx={{ width: 180, flex: '0 0 auto' }}
        >
          {STATUS_FILTERS.map(option => (
            <MenuItem key={option.id} value={option.id}>{option.name}</MenuItem>
          ))}
        </TextField>
      </Box>

      {data?.summary && (
        <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
          <Chip size="small" label={`${data.summary.total} cobranças`} />
          <Chip size="small" color="success" label={`${data.summary.paid} pagas`} />
          <Chip size="small" label={`${data.summary.open} em aberto`} />
          {data.summary.overdue > 0 && <Chip size="small" color="error" label={`${data.summary.overdue} em atraso`} />}
          {data.summary.installation > 0 && <Chip size="small" color="secondary" variant="outlined" label={`${data.summary.installation} implantação`} />}
          <Chip size="small" variant="outlined" label={`Total ${brl(data.summary.totalValue)}`} />
        </Box>
      )}

      {loading && <CircularProgress size={22} />}

      {!loading && (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Vencimento</TableCell>
              <TableCell>Competência</TableCell>
              <TableCell align="right">Valor</TableCell>
              <TableCell>Tipo</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Liquidação</TableCell>
              <TableCell>Forma</TableCell>
              <TableCell>Loja</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(data?.items || []).map(item => {
              const badge = RECEIVABLE_STATUS_LABELS[item.status] || RECEIVABLE_STATUS_LABELS[ReceivableStatus.OPEN];
              const kind = RECEIVABLE_TYPE_LABELS[item.type] || RECEIVABLE_TYPE_LABELS[ReceivableType.UNKNOWN];
              return (
                <TableRow key={item.id} hover>
                  <TableCell>{asDate(item.dueDate)}</TableCell>
                  <TableCell>{asDate(item.competenceDate)}</TableCell>
                  <TableCell align="right">{brl(item.value)}</TableCell>
                  <TableCell>
                    {/* A descrição do ERP é a origem da dedução — no title para conferência. */}
                    <Chip size="small" variant="outlined" label={kind.label} color={kind.color} title={item.description || ''} />
                  </TableCell>
                  <TableCell><Chip size="small" label={badge.label} color={badge.color} /></TableCell>
                  <TableCell>{asDate(item.paymentDate)}</TableCell>
                  <TableCell>{item.paymentMethod || '—'}</TableCell>
                  <TableCell>{item.storeName || '—'}</TableCell>
                </TableRow>
              );
            })}
            {!data?.items?.length && (
              <TableRow>
                <TableCell colSpan={8}>
                  <Typography variant="body2" color="text.secondary">Nenhuma cobrança nesse filtro.</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      )}

      {data?.window && (
        <Typography variant="caption" color="text.secondary">
          Janela consultada: {data.window.startDate} a {data.window.endDate}. Boleto pago antes do início
          da janela não é devolvido pelo Gestão Click.
        </Typography>
      )}
    </Paper>
  );
};

/**
 * Liga o faturamento recorrente pela PontuaX.
 *
 * Ligar sem desativar a assinatura no Gestão Click cobra o posto duas vezes — e a assinatura não
 * pode ser desativada por API. O backend bloqueia quando detecta assinatura ativa; o botão de
 * forçar existe porque a detecção depende da descrição do lançamento, que nem sempre é fiel.
 */
const BillingSourceCard = ({ placeId, billingSource, onChanged }) => {
  const notify = useNotify();
  const [busy, setBusy] = useState(false);
  const [conflito, setConflito] = useState(null);
  const ligado = billingSource === BillingSource.PONTUAX;

  const alternar = async (force = false) => {
    setBusy(true);
    setConflito(null);
    try {
      await httpClientReactAdmin(`${DEFAULT_API_ADMIN}/places/${placeId}/gestao-click/billing-source`, {
        method: 'POST',
        body: JSON.stringify({
          billingSource: ligado ? BillingSource.SUBSCRIPTION : BillingSource.PONTUAX,
          ...(force && { force: true }),
        }),
      });
      notify(ligado ? 'Faturamento devolvido à assinatura do ERP' : 'Faturamento recorrente ligado', { type: 'success' });
      onChanged();
    } catch (error) {
      const msg = errorMessage(error, 'Não foi possível alterar');
      if (error?.status === 409) setConflito(msg); else notify(msg, { type: 'warning' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
      <SectionTitle
        action={
          <Button size="small" variant={ligado ? 'outlined' : 'contained'} onClick={() => alternar(false)} disabled={busy}>
            {ligado ? 'Devolver à assinatura do ERP' : 'Ligar faturamento pela PontuaX'}
          </Button>
        }
      >
        Faturamento recorrente
      </SectionTitle>

      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1 }}>
        <Chip size="small" color={ligado ? 'success' : 'default'} label={ligado ? 'Gerado pela PontuaX' : 'Assinatura do Gestão Click'} />
      </Box>
      <Typography variant="body2" color="text.secondary">
        {ligado
          ? 'A mensalidade é gerada todo dia 1º pelo nosso worker, com a competência do mês de referência.'
          : 'A mensalidade é gerada pela assinatura do ERP, que carimba a competência com a data em que o boleto é emitido.'}
      </Typography>

      {conflito && (
        <Alert
          severity="warning"
          sx={{ mt: 2 }}
          action={<Button size="small" color="inherit" onClick={() => alternar(true)} disabled={busy}>Ligar assim mesmo</Button>}
        >
          <AlertTitle>Assinatura ativa no Gestão Click</AlertTitle>
          {conflito}
        </Alert>
      )}
    </Paper>
  );
};

/** Geração de cobrança. Só aparece depois do vínculo. */
const NewCharge = ({ placeId, contract, stores, storeSaved, currentStore, installationCount, onCreated }) => {
  const notify = useNotify();
  const [type, setType] = useState(ReceivableType.MONTHLY);
  const [value, setValue] = useState(contract?.monthlyPrice ?? '');

  /** Valor sugerido pelo cadastro do posto para cada tipo de cobrança. */
  const valorSugerido = tipo => (
    tipo === ReceivableType.INSTALLATION ? contract?.installationCost : contract?.monthlyPrice
  ) ?? '';

  /**
   * Trocar o tipo troca o valor sugerido junto. Sem isso, escolher "Implantação" mantinha o valor
   * da mensalidade no campo e a cobrança saía pelo preço errado.
   */
  const handleTypeChange = novoTipo => {
    setType(novoTipo);
    setValue(atual => (String(atual) === String(valorSugerido(type)) ? valorSugerido(novoTipo) : atual));
  };
  const [dueDate, setDueDate] = useState(
    moment().add(1, 'month').date(contract?.monthlyDueDate || 10).format('YYYY-MM-DD')
  );
  const [installments, setInstallments] = useState(1);
  // A loja define conta bancária e forma de pagamento. Só é perguntada quando o posto não tem uma
  // gravada — vínculo antigo, feito antes de guardarmos essa escolha.
  const [storeId, setStoreId] = useState(currentStore?.id || '');
  const [busy, setBusy] = useState(false);
  const [confirmandoSemImplantacao, setConfirmandoSemImplantacao] = useState(false);

  /**
   * Mensalidade num posto que nunca teve implantação é suspeita: normalmente a implantação vem
   * primeiro, e o tipo errado só se descobre depois do boleto no banco. Confirma antes.
   */
  const podeSerEngano = type === ReceivableType.MONTHLY && installationCount === 0;

  const handleSubmit = () => {
    if (podeSerEngano) { setConfirmandoSemImplantacao(true); return; }
    submit();
  };

  const submit = async () => {
    setConfirmandoSemImplantacao(false);
    setBusy(true);
    try {
      await httpClientReactAdmin(`${DEFAULT_API_ADMIN}/places/${placeId}/gestao-click/charges`, {
        method: 'POST',
        body: JSON.stringify({
          type,
          value: Number(value),
          dueDate,
          ...(!storeSaved && storeId && { storeId }),
          ...(type === ReceivableType.INSTALLATION && { installments: Number(installments) }),
        }),
      });
      notify('Cobrança criada no Gestão Click', { type: 'success' });
      onCreated();
    } catch (error) {
      notify(errorMessage(error, 'Não foi possível criar a cobrança'), { type: 'warning' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
      <SectionTitle>Gerar cobrança</SectionTitle>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Valor e vencimento vêm do cadastro do posto e podem ser ajustados aqui. Conta bancária, forma de
        pagamento e plano de contas são definidos pela loja: PontuaX emite em Bradesco - PontuaX,
        PontuaX Tec em Bradesco - PontuaX Tec.
      </Typography>
      <Grid container spacing={2} sx={{ maxWidth: 760 }}>
        <Grid size={{ xs: 12, md: 3 }}>
          <TextField select fullWidth size="small" label="Tipo" value={type} onChange={e => handleTypeChange(e.target.value)}>
            <MenuItem value={ReceivableType.MONTHLY}>Mensalidade</MenuItem>
            <MenuItem value={ReceivableType.INSTALLATION}>Implantação</MenuItem>
          </TextField>
        </Grid>
        <Grid size={{ xs: 12, md: 3 }}>
          <TextField
            fullWidth size="small" type="number" label="Valor total (R$)"
            value={value} onChange={e => setValue(e.target.value)}
            helperText={
              valorSugerido(type) === ''
                ? (type === ReceivableType.INSTALLATION
                    ? 'Posto sem valor de instalação no cadastro'
                    : 'Posto sem mensalidade no cadastro')
                : `Cadastro do posto: ${brl(Number(valorSugerido(type)))}`
            }
          />
        </Grid>
        <Grid size={{ xs: 12, md: 3 }}>
          <TextField
            fullWidth size="small" type="date" label="Vencimento"
            InputLabelProps={{ shrink: true }}
            value={dueDate} onChange={e => setDueDate(e.target.value)}
          />
        </Grid>
        {!storeSaved && (
          <Grid size={{ xs: 12, md: 3 }}>
            <TextField
              select fullWidth size="small" label="Loja" value={storeId}
              onChange={e => setStoreId(e.target.value)}
              helperText="Define a conta bancária do boleto"
            >
              {(stores || []).map(store => (
                <MenuItem key={store.id} value={store.id}>{store.name}</MenuItem>
              ))}
            </TextField>
          </Grid>
        )}
        {type === ReceivableType.INSTALLATION && (
          <Grid size={{ xs: 12, md: 3 }}>
            <TextField select fullWidth size="small" label="Parcelas" value={installments} onChange={e => setInstallments(e.target.value)}>
              <MenuItem value={1}>1x</MenuItem>
              <MenuItem value={2}>2x</MenuItem>
            </TextField>
          </Grid>
        )}
      </Grid>
      {type === ReceivableType.INSTALLATION && Number(installments) > 1 && Number(value) > 0 && (
        <Alert severity="info" sx={{ mt: 2 }}>
          O Gestão Click divide o total pelo número de parcelas:{' '}
          <strong>{installments}x de {brl(Number(value) / Number(installments))}</strong>. As parcelas
          seguem o intervalo da forma de pagamento do posto — 30 dias nos boletos da PontuaX.
        </Alert>
      )}

      <Box sx={{ mt: 2 }}>
        <Button variant="contained" startIcon={<ReceiptLongIcon />} onClick={handleSubmit} disabled={busy || !value || !dueDate || (!storeSaved && !storeId)}>
          {busy ? 'Enviando...' : 'Gerar no Gestão Click'}
        </Button>
      </Box>

      <Dialog open={confirmandoSemImplantacao} onClose={() => setConfirmandoSemImplantacao(false)}>
        <DialogTitle>Este posto nunca teve cobrança de implantação</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Você está gerando uma <strong>mensalidade</strong> de {brl(Number(value))} com vencimento em{' '}
            {asDate(dueDate)}, mas não há nenhuma cobrança de implantação neste posto.
          </DialogContentText>
          <DialogContentText sx={{ mt: 2 }}>
            Normalmente a implantação vem primeiro. Confirma que é mensalidade mesmo?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmandoSemImplantacao(false)}>Cancelar</Button>
          <Button
            onClick={() => { handleTypeChange(ReceivableType.INSTALLATION); setConfirmandoSemImplantacao(false); }}
          >
            Trocar para implantação
          </Button>
          <Button variant="contained" onClick={submit}>Sim, é mensalidade</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

const Linked = ({ placeId, state, onChanged }) => {
  const notify = useNotify();
  const [busy, setBusy] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const { customer, store, contract, gestaoClickId, missingInErp, stores, storeSaved, installationCount, billingSource } = state;

  const unlink = async () => {
    setBusy(true);
    try {
      await httpClientReactAdmin(`${DEFAULT_API_ADMIN}/places/${placeId}/gestao-click/link`, { method: 'DELETE' });
      notify('Vínculo removido', { type: 'success' });
      onChanged();
    } catch (error) {
      notify(errorMessage(error, 'Não foi possível remover o vínculo'), { type: 'warning' });
    } finally {
      setBusy(false);
    }
  };

  if (missingInErp) {
    return (
      <Alert severity="error" action={<Button size="small" onClick={unlink} disabled={busy}>Remover vínculo</Button>}>
        <AlertTitle>Cliente {gestaoClickId} não existe mais no Gestão Click</AlertTitle>
        O posto aponta para um cadastro que sumiu do ERP. Remova o vínculo e refaça.
      </Alert>
    );
  }

  return (
    <>
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <SectionTitle
          action={
            <Button size="small" color="inherit" startIcon={<LinkOffIcon />} onClick={unlink} disabled={busy}>
              Desvincular
            </Button>
          }
        >
          Cliente no Gestão Click
        </SectionTitle>
        <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
          <Chip size="small" label={`ID ${customer.id}`} />
          <Chip size="small" color={customer.isActive ? 'success' : 'default'} label={customer.isActive ? 'Ativo' : 'Inativo'} />
          {store && <Chip size="small" variant="outlined" label={`Loja ${store.name}`} />}
        </Box>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Field label="Razão Social">{customer.corporateName || customer.name}</Field>
            <Field label="CPF/CNPJ">{customer.document}</Field>
            <Field label="E-mail">{customer.email}</Field>
            <Field label="Telefone">{customer.phone}</Field>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Field label="Cidade">{customer.city ? `${customer.city}/${customer.state}` : null}</Field>
            <Field label="Mensalidade">{contract?.monthlyPrice ? brl(contract.monthlyPrice) : null}</Field>
            <Field label="Dia de vencimento">{contract?.monthlyDueDate}</Field>
            <Field label="Início do faturamento">{asDate(contract?.billingStartDate)}</Field>
          </Grid>
        </Grid>
        {!store && (
          <Typography variant="caption" color="text.secondary">
            Loja não identificada — o cadastro do cliente não guarda loja no Gestão Click; ela é deduzida
            das cobranças, e este posto ainda não tem nenhuma.
          </Typography>
        )}
      </Paper>

      <BillingSourceCard placeId={placeId} billingSource={billingSource} onChanged={onChanged} />
      <NewCharge placeId={placeId} contract={contract} stores={stores} storeSaved={storeSaved} currentStore={store} installationCount={installationCount} onCreated={() => setReloadKey(k => k + 1)} />
      <Receivables key={reloadKey} placeId={placeId} />
    </>
  );
};

export const GestaoClickStation = () => {
  const record = useRecordContext();
  const notify = useNotify();
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const placeId = record?.id;

  const load = useCallback(async () => {
    if (!placeId) return;
    setLoading(true);
    try {
      const { json } = await httpClientReactAdmin(`${DEFAULT_API_ADMIN}/places/${placeId}/gestao-click`);
      setState(json);
    } catch (error) {
      notify(errorMessage(error, 'Não foi possível carregar a integração'), { type: 'warning' });
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [placeId]);

  useEffect(() => { load(); }, [load]);

  if (loading && !state) return <CircularProgress size={24} />;
  if (!state) return null;

  return (
    <Box sx={{ width: '100%', maxWidth: 1100 }}>
      {state.linked
        ? <Linked placeId={placeId} state={state} onChanged={load} />
        : <NotLinked placeId={placeId} contract={state.contract} stores={state.stores} onChanged={load} />}
      <Divider sx={{ my: 2 }} />
      <Typography variant="caption" color="text.secondary">
        Boleto e nota fiscal continuam na interface do Gestão Click — a API não expõe link de boleto, e a
        listagem de NFS-e não aceita filtro por cliente.
      </Typography>
    </Box>
  );
};

export default GestaoClickStation;
